import { useMemo } from 'react'
import { HStack, Text, Tooltip } from '@chakra-ui/react'
import { formatBytes } from '@core/utils/format'

// HTTP transfer limit (same as Cloudflare Workers limit, capped for reliability)
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024 // 25 MB

interface StorageUsage {
  storageUsed: number
  maxStorage: number
}

interface EditorSizeFooterProps {
  content: string
  usage?: StorageUsage | null
}

export function EditorSizeFooter({ content, usage }: EditorSizeFooterProps) {
  // Calculate line count (minimum 1 line)
  const lines = content.split('\n')
  const lineCount = Math.max(lines.length, 1)

  // Calculate byte size (characters ≠ bytes for unicode)
  const byteSize = useMemo(() => {
    return new TextEncoder().encode(content).length
  }, [content])

  // Check if this file would exceed HTTP limit
  const exceedsHttpLimit = byteSize > MAX_FILE_SIZE_BYTES

  // Calculate storage status (if usage available)
  const storageStatus = useMemo(() => {
    if (!usage) return null

    const projectedUsage = usage.storageUsed + byteSize
    const percentage = projectedUsage / usage.maxStorage

    return {
      projectedUsage,
      percentage,
      wouldExceed: projectedUsage > usage.maxStorage,
      isWarning: percentage > 0.8 && percentage <= 1,
    }
  }, [usage, byteSize])

  // Determine color based on storage limit proximity
  const sizeColor = useMemo(() => {
    if (exceedsHttpLimit) return 'red.500'
    if (storageStatus?.wouldExceed) return 'red.500'
    if (storageStatus?.isWarning) return 'orange.400'
    return 'stone.500'
  }, [exceedsHttpLimit, storageStatus])

  // Build warning message
  const warningMessage = useMemo(() => {
    if (exceedsHttpLimit) return ' (exceeds 25 MB limit)'
    if (storageStatus?.wouldExceed) {
      return ` (exceeds ${formatBytes(usage!.maxStorage)} storage limit)`
    }
    return null
  }, [exceedsHttpLimit, storageStatus, usage])

  const showWarning = exceedsHttpLimit || storageStatus?.wouldExceed || storageStatus?.isWarning

  return (
    <HStack
      pl={1}
      pr={3}
      py={1}
      bg="bg.surface"
      justify="space-between"
      spacing={4}
      borderTopWidth="1px"
      borderColor="editor.border"
    >
      <Text fontSize="xs" color="stone.500">
        {content.length > 0
          ? `${lineCount} ${lineCount === 1 ? 'line' : 'lines'} · ${content.length.toLocaleString()} chars`
          : 'No content yet'}
      </Text>
      <HStack spacing={2}>
        <Text fontSize="xs" color={sizeColor} fontWeight={showWarning ? 'medium' : 'normal'}>
          {formatBytes(byteSize)}
          {warningMessage}
        </Text>
        {usage && !warningMessage && (
          <Tooltip
            label={`Storage: ${formatBytes(usage.storageUsed)} / ${formatBytes(usage.maxStorage)} used`}
            placement="top"
            hasArrow
          >
            <Text fontSize="xs" color="stone.500" cursor="help">
              · {formatBytes(usage.storageUsed)} / {formatBytes(usage.maxStorage)}
            </Text>
          </Tooltip>
        )}
      </HStack>
    </HStack>
  )
}
